import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import { InsightArticlePage } from './InsightArticlePage';
import { InsightsPage } from './InsightsPage';
import { Page01Hero } from './Page01Hero';
import { SiteHeader } from './SiteHeader';
import { getInsight, insights } from './content/insights';
import { Page02YourEngine } from './generated/Page02YourEngine';
import { Page03WhyTheOtherStuffDidnTWork } from './generated/Page03WhyTheOtherStuffDidnTWork';
import { Page04WhatWeActuallyTrain } from './generated/Page04WhatWeActuallyTrain';
import { Page05WhereItComesFrom } from './generated/Page05WhereItComesFrom';
import { Page06WhatHouse45Is } from './generated/Page06WhatHouse45Is';
import { Page07Day46 } from './generated/Page07Day46';
import { Page08StartTomorrowMorning } from './generated/Page08StartTomorrowMorning';
import { Page09WhoThisIsFor } from './generated/Page09WhoThisIsFor';
import { Page10StraightAnswers } from './generated/Page10StraightAnswers';
import { Page11TheDoor } from './generated/Page11TheDoor';

const navigationTargets: Record<string, number> = {
  IMTHEHOUSE: 1,
  METHOD: 4,
  'HOUSE 45': 6,
  FAQ: 10,
  ASSESSMENT: 11,
  'FULL FAQ →': 10,
};

const scenes = [
  Page02YourEngine,
  Page03WhyTheOtherStuffDidnTWork,
  Page04WhatWeActuallyTrain,
  Page05WhereItComesFrom,
  Page06WhatHouse45Is,
  Page07Day46,
  Page08StartTomorrowMorning,
  Page09WhoThisIsFor,
  Page10StraightAnswers,
];

const normalizePath = (path: string) => path.length > 1 ? path.replace(/\/+$/, '') : path;

export function App() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));
  const [pendingHomeScene, setPendingHomeScene] = useState<number | null>(null);
  const [assessmentRequest, setAssessmentRequest] = useState(0);
  const siteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPopState = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const go = useCallback((nextPath: string) => {
    const normalized = normalizePath(nextPath);
    if (normalizePath(window.location.pathname) !== normalized) window.history.pushState({}, '', normalized);
    setPath(normalized);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const scrollToScene = useCallback((scene: number) => {
    const target = siteRef.current?.querySelector<HTMLElement>(`#scene-${String(scene).padStart(2, '0')}`);
    target?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  const goHomeScene = useCallback((scene: number) => {
    if (path === '/') {
      scrollToScene(scene);
      return;
    }
    setPendingHomeScene(scene);
    go('/');
  }, [go, path, scrollToScene]);

  useEffect(() => {
    if (path !== '/' || pendingHomeScene === null) return;
    const frame = window.requestAnimationFrame(() => {
      scrollToScene(pendingHomeScene);
      setPendingHomeScene(null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [path, pendingHomeScene, scrollToScene]);

  const beginAssessment = useCallback(() => {
    setAssessmentRequest((request) => request + 1);
    goHomeScene(11);
  }, [goHomeScene]);

  const headerProps = {
    onHome: () => goHomeScene(1),
    onMethod: () => goHomeScene(4),
    onHouse45: () => goHomeScene(6),
    onFaq: () => goHomeScene(10),
    onInsights: () => go('/insights'),
    onAssessment: beginAssessment,
    active: path.startsWith('/insights') ? 'insights' as const : path === '/' ? 'home' as const : undefined,
  };

  const articleSlug = path.startsWith('/insights/') ? path.slice('/insights/'.length) : '';
  const article = articleSlug ? getInsight(articleSlug) : undefined;
  const nextArticle = useMemo(() => {
    if (!article) return insights[0];
    const index = insights.findIndex((item) => item.slug === article.slug);
    return insights[(index + 1) % insights.length];
  }, [article]);

  return (
    <>
      <SiteHeader {...headerProps} />
      {path === '/insights' && <InsightsPage onOpenArticle={(slug) => go(`/insights/${slug}`)} onAssessment={beginAssessment} />}
      {article && <InsightArticlePage insight={article} nextInsight={nextArticle} onBack={() => go('/insights')} onOpenArticle={(slug) => go(`/insights/${slug}`)} onAssessment={beginAssessment} />}
      {path.startsWith('/insights/') && !article && <NotFound onBack={() => go('/insights')} />}
      {path === '/' && <Homepage siteRef={siteRef} scrollToScene={scrollToScene} assessmentRequest={assessmentRequest} beginAssessment={beginAssessment} go={go} />}
      {!path.startsWith('/insights') && path !== '/' && <NotFound onBack={() => go('/')} />}
    </>
  );
}

type HomepageProps = {
  siteRef: RefObject<HTMLDivElement | null>;
  scrollToScene: (scene: number) => void;
  assessmentRequest: number;
  beginAssessment: () => void;
  go: (path: string) => void;
};

function Homepage({ siteRef, scrollToScene, assessmentRequest, beginAssessment, go }: HomepageProps) {
  const [scrollProgress, setScrollProgress] = useState(0);

  const routeRepeatedNavigation = (event: ReactMouseEvent<HTMLDivElement>) => {
    const button = (event.target as HTMLElement).closest('button');
    if (!button || button.closest('.site-header')) return;
    const label = button.textContent?.replace(/\s+/g, ' ').trim().toUpperCase() ?? '';
    if (label === 'INSIGHTS') {
      go('/insights');
      return;
    }
    const target = navigationTargets[label];
    if (target) window.requestAnimationFrame(() => scrollToScene(target));
  };

  useEffect(() => {
    const measure = () => {
      const documentHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const max = Math.max(documentHeight - window.innerHeight, 1);
      setScrollProgress(Math.min(100, Math.max(0, window.scrollY / max * 100)));
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#scene-02">Skip the opening scene</a>
      <div className="site" ref={siteRef} onClickCapture={routeRepeatedNavigation}>
        <section className="site-scene site-scene--01" id="scene-01" data-scene={1}>
          <Page01Hero onNavigate={scrollToScene} onAssessment={beginAssessment} />
        </section>
        {scenes.map((Scene, index) => {
          const sceneNumber = index + 2;
          return (
            <section
              key={sceneNumber}
              className={`site-scene site-scene--chapter site-scene--${String(sceneNumber).padStart(2, '0')}`}
              id={`scene-${String(sceneNumber).padStart(2, '0')}`}
              data-scene={sceneNumber}
            >
              <Scene />
            </section>
          );
        })}
        <section className="site-scene site-scene--chapter site-scene--11" id="scene-11" data-scene={11}>
          <Page11TheDoor assessmentRequest={assessmentRequest} />
        </section>
      </div>
      <div className="site-progress" aria-hidden="true"><span style={{ width: `${scrollProgress}%` }} /></div>
    </>
  );
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <main className="not-found">
      <span>404</span>
      <h1>NOTHING RUNNING HERE.</h1>
      <button className="site-button site-button-primary" type="button" onClick={onBack}>RETURN TO THE HOUSE</button>
    </main>
  );
}
